import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Button, Col, ListGroup, Row} from 'react-bootstrap';
import {FormikErrors, useFormikContext, getIn} from 'formik';
import {Icon} from 'shared/helpers';
import {MIMETypes} from 'shared/MIMETypes';
import {extname, basename} from 'path';
import {ErrorHandler} from 'shared/components/ErrorHandler';
import FileSelector from 'components/customer/Job/components/FileSelector'; // maybe move this in this directory later

interface FileUploadProps {
    fileUploadErrors: FormikErrors<{[key: string]: unknown}>;
    multiple?: boolean;

    attachedFiles?: string[];
    deleteAttachedFileCallback?: (index?: number) => void;
    name: string;
    displayError?: boolean;
}

export const FileUpload = ({
    fileUploadErrors,
    multiple = true,

    attachedFiles,
    deleteAttachedFileCallback,
    name,
    displayError = true,
}: FileUploadProps) => {
    const {values, touched, setFieldValue} = useFormikContext<{
        [key: string]: File[] | File;
    }>();
    const [files, setFiles] = useState<File[]>([]);
    const [attachments, setAttachments] = useState<string[]>([]);
    const fileFormats = useRef(Object.keys(MIMETypes)).current;

    useEffect(() => {
        if (attachedFiles) {
            setAttachments(attachedFiles);
        }
    }, [attachedFiles]);

    const isFileArray = useCallback((value: unknown) => {
        return (
            Array.isArray(value) && value.every((item) => item instanceof File)
        );
    }, []);

    const value = getIn(values, name) as unknown;

    useEffect(() => {
        if (isFileArray(value)) {
            setFiles(value);
        } else if (value instanceof File) {
            setFiles([value]);
        } else {
            setFiles([]);
        }
    }, [value]);

    const fileHandler = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target?.files) {
                const selectedFiles = Array.from(event.target.files).filter(
                    (file) => {
                        const selectedPrevious = files.map((file) => file.name);

                        return !selectedPrevious.includes(file.name);
                    }
                );

                if (multiple) {
                    const allFiles = [...files, ...selectedFiles];
                    void setFieldValue(name, allFiles);
                } else {
                    void setFieldValue(name, selectedFiles[0]);
                }
            }
        },
        [files, multiple]
    );

    const fileSizeFormatter = useCallback((size: number) => {
        return size / 1024 > 1024
            ? `${(size / (1024 * 1024)).toFixed(2)} MB`
            : `${(size / 1024).toFixed(2)} KB`;
    }, []);

    const removeFileFromList = useCallback(
        (index: number) => () => {
            if (multiple) {
                files.splice(index, 1);
                void setFieldValue(name, files);
            } else {
                void setFieldValue(name, null);
            }
        },
        [files, multiple]
    );

    const getExtensionFromPath = useCallback((file: string) => {
        return extname(file).split('.').pop();
    }, []);

    const deleteUploadedFile = useCallback(
        (index: number) => () => {
            if (deleteAttachedFileCallback) {
                deleteAttachedFileCallback(index);
            }
        },
        [deleteAttachedFileCallback]
    );

    return (
        <div className="fileContainer">
            {(!multiple && attachments.length == 0 && files.length == 0) ||
            multiple ? (
                <FileSelector
                    fileHandler={fileHandler}
                    fileFormats={fileFormats}
                    multiple={multiple}
                />
            ) : null}
            {displayError ? (
                <Row className="jobFormErrors">
                    <Col>
                        <ErrorHandler
                            errors={fileUploadErrors}
                            touched={touched}
                            style={{marginTop: '15px'}}
                        />
                    </Col>
                </Row>
            ) : null}
            {attachments.length ? (
                <ListGroup>
                    {attachments.map((file, index) => {
                        return (
                            <ListGroup.Item key={file} className="fileListItem">
                                <span className="type">
                                    {getExtensionFromPath(file)}
                                </span>
                                <div className="nameSize">
                                    <div>{basename(file)}</div>
                                </div>
                                <div>
                                    <Button
                                        variant="link"
                                        onClick={deleteUploadedFile(index)}>
                                        <Icon iconName="Button-Cancel.svg" />
                                    </Button>
                                </div>
                            </ListGroup.Item>
                        );
                    })}
                </ListGroup>
            ) : null}
            {files.length ? (
                <ListGroup>
                    {files.map((file, index) => {
                        return (
                            <ListGroup.Item
                                key={file.name}
                                className="fileListItem">
                                <span className="type">
                                    {getExtensionFromPath(file.name)}
                                </span>
                                <div className="nameSize">
                                    <div>{file.name}</div>
                                    <div>{fileSizeFormatter(file.size)}</div>
                                </div>
                                <div>
                                    <Button
                                        variant="link"
                                        onClick={removeFileFromList(index)}>
                                        <Icon iconName="Button-Cancel.svg" />
                                    </Button>
                                </div>
                            </ListGroup.Item>
                        );
                    })}
                </ListGroup>
            ) : null}
        </div>
    );
};
